import { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, Clock, Moon, Sun, Activity, TrendingUp, AlertCircle, Check, RefreshCw, LogIn, User, ChevronDown } from 'lucide-react';

// API endpoint - would point to your Claudeflare worker
const API_ENDPOINT ='https://summer-sunset-5a10.fk524097.workers.dev/';

// Fallback data in case API call fails
const fallbackWeeklyData = [
  { day: 'Monday', sleepDuration: 7.5, sleepQuality: 85, bedTime: '23:30', wakeTime: '07:00' },
  { day: 'Tuesday', sleepDuration: 6.2, sleepQuality: 72, bedTime: '00:15', wakeTime: '06:30' },
  { day: 'Wednesday', sleepDuration: 8.1, sleepQuality: 90, bedTime: '22:45', wakeTime: '06:50' },
  { day: 'Thursday', sleepDuration: 5.8, sleepQuality: 65, bedTime: '01:20', wakeTime: '07:10' },
  { day: 'Friday', sleepDuration: 7.2, sleepQuality: 78, bedTime: '23:15', wakeTime: '06:30' },
  { day: 'Saturday', sleepDuration: 9.0, sleepQuality: 95, bedTime: '23:00', wakeTime: '08:00' },
  { day: 'Sunday', sleepDuration: 8.5, sleepQuality: 88, bedTime: '22:30', wakeTime: '07:00' },
];

const fallbackFeedback = {
  overview: "Your sleep patterns this week show moderate consistency with some room for improvement.",
  insights: [
    "Your average sleep duration of 7.5 hours is within the recommended range.",
    "Sleep quality fluctuates significantly mid-week, potentially due to inconsistent bedtimes.",
    "Weekend recovery sleep pattern detected - try to maintain more consistent sleep schedule."
  ],
  recommendations: [
    "Aim for a consistent bedtime between 22:30-23:00.",
    "Reduce screen time within 1 hour of bedtime.",
    "Consider light stretching before bed to improve sleep quality."
  ],
  sleepScore: 82
};

export default function SleepAnalysisDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState([]);
  const [feedback, setFeedback] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Fetch sleep data from API
  const fetchData = async (userEmail) => {
    if (!userEmail) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_ENDPOINT}?email=${encodeURIComponent(userEmail)}`);
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      
      const userData = await response.json();
      
      // Update state with fetched data
      setData(userData.weeklyData || fallbackWeeklyData);
      setFeedback(userData.feedback || fallbackFeedback);
      setUser({
        name: userData.name || userEmail.split('@')[0],
        email: userEmail
      });
      
      // Store user email in localStorage for persistence
      localStorage.setItem('sleepUserEmail', userEmail);
      setIsLoggedIn(true);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      setError("Failed to load your sleep data. Please try again later.");
      
      // Use fallback data if API fails
      setData(fallbackWeeklyData);
      setFeedback(fallbackFeedback);
      
      if (userEmail) {
        setUser({
          name: userEmail.split('@')[0],
          email: userEmail
        });
        setIsLoggedIn(true);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle login
  const handleLogin = (e) => {
    e.preventDefault();
    if (email) {
      fetchData(email);
    }
  };
  
  // Handle logout
  const handleLogout = () => {
    setIsLoggedIn(false);
    setUser(null);
    localStorage.removeItem('sleepUserEmail');
  };
  
  // Check for saved email on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('sleepUserEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      fetchData(savedEmail);
    } else {
      setIsLoading(false);
    }
  }, []);

  const averageSleepDuration = data.reduce((sum, day) => sum + day.sleepDuration, 0) / data.length;
  const averageSleepQuality = data.reduce((sum, day) => sum + day.sleepQuality, 0) / data.length;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-600 text-white p-4 shadow-md">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Moon className="h-8 w-8" />
            <h1 className="text-2xl font-bold">SleepWell Analytics</h1>
          </div>
          <div className="flex items-center space-x-2">
            {isLoggedIn && user ? (
              <div className="relative">
                <button 
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center space-x-2 bg-blue-700 hover:bg-blue-800 py-2 px-3 rounded-md"
                >
                  <User className="h-5 w-5" />
                  <span>{user.name}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
                
                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                    <button 
                      onClick={() => { fetchData(user.email); setShowDropdown(false); }}
                      className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                    >
                      <RefreshCw className="h-4 w-4 inline mr-2" />
                      Refresh Data
                    </button>
                    <button 
                      onClick={() => { handleLogout(); setShowDropdown(false); }}
                      className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                    >
                      <LogIn className="h-4 w-4 inline mr-2" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button className="flex items-center space-x-1 bg-blue-700 hover:bg-blue-800 py-2 px-3 rounded-md">
                <LogIn className="h-5 w-5" />
                <span>Sign In</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto flex-grow p-6">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : !isLoggedIn ? (
          <div className="max-w-md mx-auto mt-12 bg-white p-8 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-6 text-center">Access Your Sleep Data</h2>
            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your email"
                  required
                />
              </div>
              <button 
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md font-medium"
              >
                Continue
              </button>
            </form>
            <p className="mt-4 text-center text-sm text-gray-600">
              Enter the email you used in the sleep quality survey
            </p>
          </div>
        ) : error ? (
          <div className="max-w-md mx-auto mt-12 bg-white p-8 rounded-lg shadow-md">
            <div className="text-red-500 mb-4">
              <AlertCircle className="h-10 w-10 mx-auto mb-2" />
              <p className="text-center">{error}</p>
            </div>
            <button 
              onClick={() => fetchData(user.email)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md font-medium"
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            {/* Dashboard Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 font-medium">Sleep Score</h3>
                  <Activity className="h-5 w-5 text-blue-500" />
                </div>
                <p className="text-3xl font-bold">{feedback.sleepScore}/100</p>
                <div className="mt-2 flex items-center">
                  {feedback.sleepScore > 80 ? (
                    <span className="text-green-500 flex items-center"><Check className="h-4 w-4 mr-1" /> Good</span>
                  ) : feedback.sleepScore > 60 ? (
                    <span className="text-yellow-500 flex items-center"><AlertCircle className="h-4 w-4 mr-1" /> Fair</span>
                  ) : (
                    <span className="text-red-500 flex items-center"><AlertCircle className="h-4 w-4 mr-1" /> Poor</span>
                  )}
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 font-medium">Avg. Sleep Duration</h3>
                  <Clock className="h-5 w-5 text-blue-500" />
                </div>
                <p className="text-3xl font-bold">{averageSleepDuration.toFixed(1)} hrs</p>
                <div className="mt-2 flex items-center">
                  {averageSleepDuration >= 7 ? (
                    <span className="text-green-500 flex items-center"><Check className="h-4 w-4 mr-1" /> Recommended</span>
                  ) : (
                    <span className="text-yellow-500 flex items-center"><AlertCircle className="h-4 w-4 mr-1" /> Below target</span>
                  )}
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 font-medium">Avg. Sleep Quality</h3>
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                </div>
                <p className="text-3xl font-bold">{averageSleepQuality.toFixed(0)}%</p>
                <div className="mt-2 flex items-center">
                  {averageSleepQuality >= 80 ? (
                    <span className="text-green-500 flex items-center"><Check className="h-4 w-4 mr-1" /> Excellent</span>
                  ) : averageSleepQuality >= 70 ? (
                    <span className="text-yellow-500 flex items-center"><Check className="h-4 w-4 mr-1" /> Good</span>
                  ) : (
                    <span className="text-red-500 flex items-center"><AlertCircle className="h-4 w-4 mr-1" /> Poor</span>
                  )}
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-gray-500 font-medium">Weekly Trend</h3>
                  <Calendar className="h-5 w-5 text-blue-500" />
                </div>
                <div className="h-12">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data}>
                      <Line type="monotone" dataKey="sleepQuality" stroke="#3B82F6" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 flex items-center">
                  <span className="text-blue-500 flex items-center"><TrendingUp className="h-4 w-4 mr-1" /> View details</span>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="mb-6 border-b">
              <div className="flex space-x-8">
                <button 
                  onClick={() => setActiveTab('overview')}
                  className={`pb-4 font-medium ${activeTab === 'overview' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Weekly Overview
                </button>
                <button 
                  onClick={() => setActiveTab('details')}
                  className={`pb-4 font-medium ${activeTab === 'details' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Daily Details
                </button>
                <button 
                  onClick={() => setActiveTab('insights')}
                  className={`pb-4 font-medium ${activeTab === 'insights' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  AI Insights
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-semibold mb-4">Sleep Duration (hours)</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" />
                        <YAxis domain={[0, 10]} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="sleepDuration" fill="#3B82F6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-semibold mb-4">Sleep Quality (%)</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="sleepQuality" stroke="#3B82F6" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'details' && (
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Daily Sleep Log</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bed Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wake Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quality</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data.map((day, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{day.day}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{day.bedTime}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{day.wakeTime}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{day.sleepDuration} hrs</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${day.sleepQuality}%` }}></div>
                              </div>
                              <span className="ml-2 text-sm text-gray-500">{day.sleepQuality}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'insights' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-semibold mb-4">Weekly Sleep Analysis</h3>
                  <p className="text-gray-700 mb-6">{feedback.overview}</p>
                  
                  <h4 className="font-medium text-gray-900 mb-2">Key Insights:</h4>
                  <ul className="list-disc pl-5 mb-6 space-y-2">
                    {feedback.insights.map((insight, index) => (
                      <li key={index} className="text-gray-700">{insight}</li>
                    ))}
                  </ul>
                  
                  <h4 className="font-medium text-gray-900 mb-2">Recommendations:</h4>
                  <ul className="list-disc pl-5 space-y-2">
                    {feedback.recommendations.map((rec, index) => (
                      <li key={index} className="text-gray-700">{rec}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-semibold mb-4">Sleep Schedule</h3>
                  <div className="space-y-6">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-500">Bedtime</span>
                        <span className="text-sm text-gray-700">Target: 22:30</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Moon className="h-5 w-5 text-blue-800" />
                        <div className="h-2 bg-gray-200 rounded-full flex-grow">
                          {data.map((day, i) => {
                            // Convert bedtime to position percentage (22:00 = 0%, 02:00 = 100%)
                            const [hours, mins] = day.bedTime.split(':').map(Number);
                            const totalMins = hours * 60 + mins;
                            const baseTime = 20 * 60; // 8:00 PM
                            const maxTime = baseTime + 6 * 60; // 4 hour range
                            const position = ((totalMins < baseTime ? totalMins + 24 * 60 : totalMins) - baseTime) / (maxTime - baseTime) * 100;
                            return (
                              <div 
                                key={i}
                                className="absolute h-3 w-3 bg-blue-500 rounded-full transform -translate-y-0.5"
                                style={{ left: `${Math.min(Math.max(position, 0), 100)}%` }}
                                title={`${day.day}: ${day.bedTime}`}
                              />
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-500">8:00 PM</span>
                        <span className="text-xs text-gray-500">2:00 AM</span>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-500">Wake time</span>
                        <span className="text-sm text-gray-700">Target: 06:30</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Sun className="h-5 w-5 text-yellow-500" />
                        <div className="h-2 bg-gray-200 rounded-full flex-grow relative">
                          {data.map((day, i) => {
                            // Convert wake time to position percentage (5:00 AM = 0%, 9:00 AM = 100%)
                            const [hours, mins] = day.wakeTime.split(':').map(Number);
                            const totalMins = hours * 60 + mins;
                            const baseTime = 5 * 60; // 5:00 AM
                            const maxTime = baseTime + 4 * 60; // 4 hour range
                            const position = ((totalMins - baseTime) / (maxTime - baseTime)) * 100;
                            return (
                              <div 
                                key={i}
                                className="absolute h-3 w-3 bg-yellow-500 rounded-full transform -translate-y-0.5"
                                style={{ left: `${Math.min(Math.max(position, 0), 100)}%` }}
                                title={`${day.day}: ${day.wakeTime}`}
                              />
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-500">5:00 AM</span>
                        <span className="text-xs text-gray-500">9:00 AM</span>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Consistency Score</h4>
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 rounded-full border-4 border-blue-500 flex items-center justify-center">
                          <span className="text-xl font-bold text-blue-600">76%</span>
                        </div>
                        <div>
                          <p className="text-sm text-gray-700">Your sleep schedule is fairly consistent. Try to keep your bedtime within 30 minutes each day.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 border-t border-gray-200 py-4">
        <div className="container mx-auto px-6 text-center text-gray-600 text-sm">
          <p>© 2025 SleepWell Analytics. All data is handled according to our privacy policy.</p>
          <p className="mt-1">Powered by Claudeflare</p>
        </div>
      </footer>
    </div>
  );
}